import { z } from 'zod';
import { BILLING_INTERVALS, PAID_PLAN_IDS } from './account';
import { DESKTOP_PLATFORMS } from './desktop-installers';
import { LANGUAGES } from './options';
import { RewritesSchema } from './rewrite';

// What Lyrmo's own clients, the site and the desktop app, tell the API they did, for the admin's
// insights: that something happened, never the text it happened to.

/**
 * The site's pages, where a visit can start. 'chrome-extension' was one until 2026-10-28: kept so
 * what was noted there still reads.
 */
export const SITE_PAGES = [
  'home',
  'studio',
  'pricing',
  'desktop-apps',
  'how-it-works',
  'chrome-extension',
] as const;
export type SitePage = (typeof SITE_PAGES)[number];

/** How text first came into the editor. */
export const TEXT_METHODS = ['type', 'paste', 'file', 'dictation', 'version'] as const;
export type TextMethod = (typeof TEXT_METHODS)[number];

/** The ways to look closer at a version: its changes, why each helps, or other ways to say them. */
export const RESULT_VIEWS = ['diff', 'why', 'alts'] as const;
export type ResultView = (typeof RESULT_VIEWS)[number];

/** What was done with a version: copied, or put in place of the text in another app. */
export const VERSION_ACTIONS = ['copy', 'insert'] as const;
export type VersionAction = (typeof VERSION_ACTIONS)[number];

/** What opened the plans: the credit limit's notice, the Upgrade button, a menu, billing, a link. */
export const PLANS_OPENERS = ['limit', 'upgrade', 'menu', 'billing', 'link', 'usage'] as const;
export type PlansOpener = (typeof PLANS_OPENERS)[number];

/** How a writer came to the desktop app's page before downloading: arriving there, or from a page. */
export const INSTALLER_FROM = ['arrival', ...SITE_PAGES] as const;
export type InstallerFrom = (typeof INSTALLER_FROM)[number];

/** How far a video was watched. */
export const VIDEO_STAGES = ['started', 'finished'] as const;
export type VideoStage = (typeof VIDEO_STAGES)[number];

/** The desktop app's operating systems. */
export const DESKTOP_SYSTEMS = ['macos', 'windows', 'linux'] as const;
export type DesktopSystem = (typeof DESKTOP_SYSTEMS)[number];

/** How visitors found Lyrmo. Visits that start in the desktop app come from the app. */
export const CHANNELS = [
  'direct',
  'search',
  'ai',
  'social',
  'referral',
  'campaign',
  'app',
] as const;
export type Channel = (typeof CHANNELS)[number];

export const DEVICES = ['computer', 'phone', 'tablet', 'app'] as const;
export type Device = (typeof DEVICES)[number];

export const OPERATING_SYSTEMS = [
  'windows',
  'macos',
  'linux',
  'chromeos',
  'ios',
  'android',
  'other',
] as const;
export type OperatingSystem = (typeof OPERATING_SYSTEMS)[number];

export const BROWSERS = [
  'chrome',
  'safari',
  'edge',
  'firefox',
  'opera',
  'samsung',
  'app',
  'other',
] as const;
export type Browser = (typeof BROWSERS)[number];

/** The longest a client may hold an event before sending it. */
export const MAX_EVENT_AGE_MS = 10 * 60_000;
/** The most events one request may carry. */
export const MAX_EVENTS_PER_REQUEST = 20;

const version = RewritesSchema.keyof();
const hostname = z
  .string()
  .max(253)
  .regex(/^[a-z0-9.-]+$/i, 'A hostname, without a scheme, path, or port.');
const tag = z.string().trim().max(64);

const when = {
  ago: z
    .int()
    .min(0)
    .max(MAX_EVENT_AGE_MS)
    .default(0)
    .describe('How many milliseconds before the request it happened.'),
};

export const ClientEventSchema = z.discriminatedUnion('event', [
  z
    .object({
      event: z.literal('visit'),
      page: z.enum(SITE_PAGES),
      referrer: hostname.optional().describe('The referring site’s hostname: no path or query.'),
      campaign: z
        .object({ source: tag.optional(), medium: tag.optional(), name: tag.optional() })
        .optional()
        .describe('The utm_source, utm_medium, and utm_campaign of the address it arrived at.'),
      language: z.enum(LANGUAGES).exclude(['auto']).describe('The language the site is shown in.'),
      ...when,
    })
    .describe('A visit began: the site was opened after half an hour or more away.'),
  z.object({ event: z.literal('page'), page: z.enum(SITE_PAGES), ...when }),
  z.object({
    event: z.literal('text'),
    method: z.enum(TEXT_METHODS),
    words: z.int().min(0).max(100_000),
    ...when,
  }),
  z.object({ event: z.literal('result'), view: z.enum(RESULT_VIEWS), version, ...when }),
  z.object({ event: z.literal('version'), version, action: z.enum(VERSION_ACTIONS), ...when }),
  z.object({ event: z.literal('plans'), from: z.enum(PLANS_OPENERS), ...when }),
  z.object({ event: z.literal('sign_in_opened'), ...when }).describe('Sign-in was opened.'),
  z
    .object({
      event: z.literal('plan_chosen'),
      plan: z.enum(PAID_PLAN_IDS),
      interval: z.enum(BILLING_INTERVALS),
      ...when,
    })
    .describe('A plan was chosen in the plans, before signing in or checking out.'),
  z
    .object({
      event: z.literal('installer'),
      platform: z.enum(DESKTOP_PLATFORMS),
      from: z.enum(INSTALLER_FROM),
      ...when,
    })
    .describe('An installer’s download was started on the desktop app’s page.'),
  z
    .object({ event: z.literal('microphone_blocked'), ...when })
    .describe('Dictation couldn’t start: the browser or system refused the microphone.'),
  z.object({
    event: z.literal('video'),
    video: z.string().regex(/^[a-z0-9-]{1,48}$/),
    stage: z.enum(VIDEO_STAGES),
    ...when,
  }),
  z
    .object({
      event: z.literal('launch'),
      version: z.string().regex(/^\d+\.\d+\.\d+$/),
      system: z.enum(DESKTOP_SYSTEMS),
      first: z.boolean().describe('Whether it’s the first time the app opened on this computer.'),
      ...when,
    })
    .describe('The desktop app started.'),
]);
export type ClientEvent = z.input<typeof ClientEventSchema>;
export type ReceivedClientEvent = z.infer<typeof ClientEventSchema>;

export const EventsRequestSchema = z.object({
  events: z.array(ClientEventSchema).min(1).max(MAX_EVENTS_PER_REQUEST),
});
export type EventsRequest = z.input<typeof EventsRequestSchema>;
